<template>
  <v-layout class="inner-pages" column>
    <Header :header-height="$auth.check() ? '70px': '120px'" />

    <scroll-data>
      <h2 class="mb-2 mt-2"><span id="link-index-certificate-program" class="page-heading">Certificate Program</span></h2>
      <div class="block">
        <div class="block-header">
          <ul class="go-to-links">
            <li @click.prevent="goTo('summary_scope')">Summary (Program Scope, Purpose & Learning Outcomes)</li>
            <li @click.prevent="goTo('participation_requirements')">Participation Requirements (Program Requisites)</li>
            <li @click.prevent="goTo('program_steps')">Program Steps to Completion (Learning Outcomes, Assessment & eBadge Term of Validity)</li>
            <li @click.prevent="goTo('training_accelerators')">Training Accelerators Help You Learn</li>
            <li @click.prevent="goTo('post_certificate')">Post Certificate Activities</li>
            <li @click.prevent="goTo('providing_feedback')">Providing Feedback and Requesting Support</li>
            <li @click.prevent="goTo('general_program_notification')">General Program Notification</li>
          </ul>
          <p id="summary_scope" class="font-weight-bold my-2">Summary (Program Scope, Purpose & Learning Outcomes)</p>
        </div>
        <p>
          <em v-text="appName" /> is an ANSI-accredited certificate program
          (<a target="_blank"
              href="https://www.ansi.org/accreditation/credentialing/certificate-issuers/AllDirectoryDetails?&prgID=212,238&OrgId=2249&statusID=4">
          Accreditation ID 1243</a>) that prepares you for successful employment and career growth in the cannabis industry.
          The certificate program has these training objectives.
        </p>

        <ol>
          <li>Expertise with a Quality Management System (QMS) and the
            <a target="_blank" href="https://www.focusstandards.org/cannabis-standards/">FOCUS Cannabis GMP </a>Standards.
          </li>
          <li>Competency with cannabis plants, compounds, products, and use.</li>
          <li>Readiness to act in key operational roles, as directed by cannabis facility management.</li>
          <li>Knowledge of common federal, state, and local laws that support cannabis-facility licensing.</li>
          <li>Understanding of safe product handling and personal hygiene practices.</li>
        </ol>
        <p>The certificate program is designed for self-guided use on a web browser or mobile device. No in-person classroom
          access is available at this time.</p>

        <p id="participation_requirements" class="font-weight-bold">Participation Requirements (Program Requisites)</p>

        <p>Here is what you need to access the Certificate Program.</p>

        <ul>
          <li>Have access to a web browser, an iPhone, iPad, or Android phone or tablet.</li>
          <li>Know how to use the browser or mobile device to access the <span v-text="appName"/> certificate program.
          </li>
          <li>Valid personal identification to include name, mailing address, email address, and phone number. This
            information will be required for online proctoring.
          </li>
          <li>Payment of the program license fee. Payment is made within the certificate program app.</li>
          <li>A 'seat time' of approximately two hours to learn, quiz, and complete the examination.</li>
        </ul>

        <p id="program_steps" class="font-weight-bold py-2">Program Steps to Completion (Learning Outcomes, Assessment & eBadge Term of Validity)</p>

        <p>All steps must be completed. There is no provision for partial work or previous experience.</p>

        <ol>
          <li>
            Access the Certificate Program
            <ol>
                 <li>From a web browser application go to <a href="https://rch.cogneti.com/" target="_blank">rch.cogneti.com</a></li>
              <li>From any connected iPhone or iPad download ‘<span v-text="appName"/>’ from the App Store</li>
              <li>From any connected Android phone or tablet download ‘<span v-text="appName"/>’ from the Google Play Store</li>
            </ol>
          </li>

          <li>
            Once the app is downloaded, you’ll be prompted to sign in or sign up using your registered email and password.
            You may also wish to add a profile picture of your choosing.
          </li>

          <li>
            Use the <span class="font-weight-bold">LEARN</span> function to view the levels of program instruction, what we call learning points or outcomes.
            <ol>
              <li>
                For each level, read the material and practice your recall by taking quizzes. Do this several times to
                increase your recall and skill. Once you complete a level of learning, the icon will turn green and the next
                program level will unlock.
              </li>
              <li>
                Those with experience in the cannabis industry may simply need a quick review. However, you must complete
                all steps and levels of the program before the exam will unlock.
              </li>
            </ol>
          </li>

          <li>
            Complete a ten-question <span class="font-weight-bold">QUIZ</span> for each of the levels of instruction. Quizzes are great preparation for the final
            exam and will increase your skill and recall across the content included in each level. Quiz questions are
            directly linked to learning points.
          </li>

          <li>
            After you have reviewed and quizzed through all levels of program instruction, you can attempt your final exam.
            The exam will test your knowledge across the entire program content.
            <ol>
              <li>You’ll need to pass the <span class="font-weight-bold">EXAM</span> with at least a 70% score.</li>
              <li>An automated email will be sent detailing your exam expertise across the program instruction. This report
                will show where you need to improve.
              </li>
              <li>If you do not pass the exam attempt, you must complete additional Learn and Quiz activities before
                unlocking another exam. These program levels will appear as red within the app.
              </li>
            </ol>
          </li>

          <li>
            When you pass the exam, you will be issued a program certificate valid for two years along with a Cogneti
            eBadge. You’ll receive the eBadge as a separate communication sent to your registered email address.
            <ol>
              <li>The eBadge works as a handy and portable form of the program certificate. It will include instructions to
                print a paper copy. You can also email it to employers, attach it to your resume, and add it to professional
                profiles such as LinkedIn.
              </li>
              <li>Your eBadge will show an expiration date that is two years after the date of your passed exam.</li>
              <li>Every eBadge includes a unique identifier number. Employers and others can use this number to verify your
                certification exists and is active at the <a href="https://cogneti.com/" target="_blank">cogneti.com</a> website.
              </li>
            </ol>
          </li>
        </ol>

        <p id="training_accelerators" class="font-weight-bold my-2">Training Accelerators Help You Learn</p>

        <p>The <span v-text="appName"/> certificate program has special features called training accelerators that will
          speed up your learning process.</p>

        <p class="mt-2 mb-1"><u>Accelerator 1:</u> Use built-in <span class="font-weight-bold">Peeks</span> to immediately locate the right information in program material to answer any
          quiz question. This is a powerful and proven method for you to quickly build reliable recall.</p>
        <ul>
          <li>Coins are earned as a reward by reading through program Learn pages.</li>
          <li>Once you have earned coins, then a Peek button appears during the quiz.</li>
          <li>Selected, the peek button to assist in answering a quiz question.</li>
        </ul>

        <p class="mt-2 mb-1"><u>Accelerator 2:</u> A <span class="font-weight-bold">Skill Level </span>shows your current learning outcome for each program level.</p>
        <ul>
          <li>Your skill level is re-calculated each time you take a level quiz.</li>
          <li>Use the quiz a few times and move closer to 100%.</li>
          <li>Your goal is to reach a skill level of Expert.</li>
        </ul>

        <p class="mt-2 mb-1"><u>Accelerator 3:</u> <span class="font-weight-bold">Quiz Time</span> measures the time you took to complete a ten-question quiz.</p>
        <ul>
          <li>Aim for a fast Quiz Time of two minutes or less.</li>
          <li>Increase your time by practicing and repeating the use of the quiz.</li>
          <li>Fast quiz times show confidence and easy subject recall.</li>
        </ul>

        <p class="mt-2 mb-1"><u>Accelerator 4:</u> <span class="font-weight-bold">Page View Tracking</span> will check off the program pages that you’ve viewed.</p>
        <ul>
          <li>Checkmarks help you track what you’ve read and learned.</li>
          <li>You can see the level pages that you still need to review.</li>
        </ul>

        <p id="post_certificate" class="font-weight-bold my-2">Post Certificate Activities</p>
        <ol>
          <li><span class="font-weight-bold">Issued Certificate:</span> When you pass the final exam, your program
            certificate is issued. It will identify you by name and shows an expiration date. It also has a unique badge ID
            that can be used to validate your certificate at
            <a href="https://cogneti.com/" target="_blank">cogneti.com</a></li>
          <li><span class="font-weight-bold">eBadge:</span> You can email, print, order a laminated form, or otherwise use
            the eBadge. It serves as portable evidence of the <span v-text="appName"/> certificate. The eBadge is sent to
            your registered email address.
          </li>
          <li><span class="font-weight-bold">Re-Issue Program Certificate:</span> The <span v-text="appName"/> certificate
            expires after two years. You may obtain a re-issued program certificate, by completing all steps of the program
            again. You will have to complete the quizzes on all program levels and, yes, you’ll also need to take and pass
            the final exam.
          </li>
          <li><span class="font-weight-bold">Practice and Skills Maintenance:</span> The <span v-text="appName"/> app is a
            great tool to practice and maintain your skills, any time and any place. If you forget something, you’ve got the
            answer in your pocket on your phone. The app is a reference for you to use.
          </li>
        </ol>

        <h4 id="providing_feedback" class="font-weight-bold py-2">Providing Feedback and Requesting Support</h4>
        <p>Cogneti is always available whenever you have questions or need some help. You can contact us through the app or by visiting the
          <a href="https://cogneti.com/" target="_blank">Cogneti website</a>.</p>
        <ul>
          <li>We strive to make our learning programs the very best and appreciate your feedback on the program.</li>
          <li>If you need technical support or find a bug please let us know so we can get every problem fixed.</li>
          <li>Should you feel you would like to appeal your grade or submit a grievance you can contact us at any time.</li>
        </ul>

        <h4 class="font-weight-bold py-2" id="general_program_notification">General Program Notification</h4>
        <p>Cogneti, LLC does not receive any commercial or third-party financial support in the creation of the certificate
          program. The awarding of a certificate is based solely on the individual performance of the student.</p>
      </div>
    </scroll-data>
    <v-btn
      v-if="scrolled > 200"
      fixed
      dark
      small
      fab
      bottom
      right
      color="blue"
      @click.prevent="$vuetify.goTo('#link-index-certificate-program')"
    >
      <v-icon>arrow_upward</v-icon>
    </v-btn>
  </v-layout>

</template>

<script>
  import Header from '../../layouts/Header'
  import ScrollData from '../../ScrollData'
  export default {
    name: 'CertificateProgram',
    components: {ScrollData, Header},
    props: {
    },
    data: () => ({
      offsetTop: 0,
      scrollHeight: 0,
      scrolled: 0,
      appName: process.env.APP_NAME
    }),
    computed: {
      appHeight: {
        get: function () {
          return this.scrollHeight
        },
        set: function (height) {
          this.scrollHeight = height
        }
      },
      styleObj () {
        const height = this.appHeight
        return {
          'height': height + 'px!important',
          // 'padding-top': '0!important',
          'padding-left': '20px!important',
          'padding-right': '20px!important',
          'overflow': 'auto'
        }
      }
    },
    created () {
      window.addEventListener('scroll', this.handleScroll)
    },
    destroyed () {
      window.removeEventListener('scroll', this.handleScroll)
    },
    mounted () {
      this.scrollHeight = this.$store.getters.getAppHeight - 190
    },
    methods: {
      goTo (elem) {
        const element = document.getElementById(elem)
        element.scrollIntoView({behavior: 'smooth'})
      },
      handleScroll () {
        this.scrolled = window.scrollY
      },
      onScroll (e) {
        const top = e.target.scrollTop
        if (top > 300) {
          this.header = false
          this.appHeight = this.$store.getters.getAppHeight
        } else {
          this.header = true
          this.appHeight = this.$store.getters.getAppHeight - 190
        }
      }
    }
  }
</script>

<style lang="stylus">
@import '~vuetify/src/stylus/main.styl';
ol ol {
  list-style: lower-alpha;
}
ul.go-to-links > li{
  color: $blue.darken-1;
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/views/guest/CertificateProgram.vue